import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { GraduationCap, Sparkles, LogOut, Search, LayoutDashboard, ShieldCheck, Briefcase, Home, MessagesSquare, Calendar, BookOpen, MessageCircleQuestion, Compass, PlayCircle, UserCog, ChevronDown, Library, MoreHorizontal, ArrowUpRight, Rocket } from 'lucide-react';
import { usePoPAuth } from '../../context/PoPAuthContext';
import { popLogin, roleLabel } from '../../lib/popApi';
import { PoPCompactNav } from '../../components/pop/PoPCompactNav';

const academyUrl = 'https://proposal-engine-49.preview.emergentagent.com/';

/**
 * Shared premium chrome for every /pop/* page.
 * Deep-navy palette with amber accents. Trust-first design.
 */
const NavLink = ({ to, icon: Icon, label, id, activeTab }) => (
  <Link
    to={to}
    data-testid={`pop-nav-${id}`}
    className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${
      activeTab === id
        ? 'bg-amber-300/10 text-amber-200 ring-1 ring-amber-300/30'
        : 'text-white/70 hover:text-white hover:bg-white/5'
    }`}
  >
    <Icon className="w-3 h-3" />
    {label}
  </Link>
);

/** Overflow menu — captures the less-essential + role-gated nav items so the
 *  primary row never overflows on 1280–1440px laptops. Same visual language as UserMenu. */
const MoreMenu = ({ items, activeTab }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);
  const activeInMore = items.some((it) => it.id === activeTab);
  if (items.length === 0) return null;
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="pop-nav-more"
        className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${
          activeInMore
            ? 'bg-amber-300/10 text-amber-200 ring-1 ring-amber-300/30'
            : 'text-white/70 hover:text-white hover:bg-white/5'
        }`}
      >
        <MoreHorizontal className="w-3 h-3" />
        More
        <ChevronDown className={`w-2.5 h-2.5 text-white/40 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-52 rounded-lg border border-white/10 bg-slate-950/95 backdrop-blur shadow-2xl overflow-hidden z-50 py-1"
          data-testid="pop-nav-more-menu"
        >
          {items.map((it) => {
            const isActive = activeTab === it.id;
            return (
              <Link
                key={it.id}
                to={it.to}
                onClick={() => setOpen(false)}
                data-testid={`pop-nav-more-${it.id}`}
                className={`flex items-center gap-2.5 px-3.5 py-2 text-xs transition ${
                  isActive
                    ? 'bg-amber-300/10 text-amber-200'
                    : 'text-white/85 hover:bg-white/5'
                }`}
              >
                <it.icon className={`w-3.5 h-3.5 ${isActive ? 'text-amber-300' : 'text-amber-300/80'}`} />
                {it.label}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

const UserMenu = ({ user, logout }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);
  const profileLabel = user.role === 'unassigned' ? 'Complete registration' : 'Edit profile';
  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="pop-user-menu-trigger"
        className="flex items-center gap-1.5 pl-0.5 pr-1.5 py-0.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 transition"
      >
        {user.picture ? (
          <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full ring-1 ring-white/20" />
        ) : (
          <div className="w-8 h-8 rounded-full bg-amber-300/20 text-amber-200 flex items-center justify-center text-xs font-bold ring-1 ring-white/20">
            {(user.name || '?').slice(0, 1).toUpperCase()}
          </div>
        )}
        <ChevronDown className={`w-3 h-3 text-white/50 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-64 rounded-lg border border-white/10 bg-slate-950/95 backdrop-blur shadow-2xl overflow-hidden z-50" data-testid="pop-user-menu">
          <div className="px-4 py-3 border-b border-white/10">
            <div className="text-xs font-semibold text-white truncate">{user.name}</div>
            <div className="text-[10px] text-white/50 truncate">{user.email}</div>
            <div className="mt-1 inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-bold text-amber-300">
              {roleLabel(user.role)}
            </div>
          </div>
          <Link
            to="/pop/onboarding"
            onClick={() => setOpen(false)}
            data-testid="pop-user-menu-profile"
            className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-white/85 hover:bg-white/5 transition"
          >
            <UserCog className="w-4 h-4 text-amber-300" />
            <div className="flex-1">
              <div>{profileLabel}</div>
              {['industry_expert', 'faculty'].includes(user.role) && !user.resume_filename && (
                <div className="text-[10px] text-amber-300/80">Upload your resume →</div>
              )}
            </div>
          </Link>
          {user.role !== 'unassigned' && (
            <Link
              to={user.role === 'super_admin' ? '/pop/admin' : '/pop/dashboard'}
              onClick={() => setOpen(false)}
              data-testid="pop-user-menu-dashboard"
              className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-white/85 hover:bg-white/5 transition border-t border-white/5"
            >
              <LayoutDashboard className="w-4 h-4 text-amber-300" />
              Dashboard
            </Link>
          )}
          <button
            onClick={() => { setOpen(false); logout(); }}
            data-testid="pop-user-menu-signout"
            className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-rose-300 hover:bg-rose-500/10 transition border-t border-white/5"
          >
            <LogOut className="w-4 h-4" />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
};

const PoPShell = ({ children, activeTab }) => {
  const { user, logout, loading } = usePoPAuth();

  return (
    <div className="min-h-screen text-white overflow-x-hidden" style={{ background: 'linear-gradient(180deg,#0B1426 0%,#0A0F1F 100%)' }}>
      {/* Top nav */}
      <header className="sticky top-0 z-40 backdrop-blur bg-slate-950/70 border-b border-white/8">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-3 px-4 sm:px-6 py-3">
          <Link to="/pop" className="flex items-center gap-2.5" data-testid="pop-brand">
            <span className="w-8 h-8 rounded-md bg-amber-300 flex items-center justify-center">
              <GraduationCap className="w-4 h-4 text-slate-900" />
            </span>
            <div className="leading-tight">
              <div className="text-[10px] tracking-[0.3em] font-bold text-amber-300">SAMANVAI</div>
              <div className="text-sm font-serif font-semibold">PoP Exchange</div>
            </div>
          </Link>

          <nav className="hidden xl:flex items-center gap-0.5 lg:gap-1">
            {/* -------- Primary items: always visible on md+ -------- */}
            <NavLink activeTab={activeTab} to="/pop"               id="home"      icon={Home}      label="Home" />
            <Link
              to="/pop/ai-mba"
              data-testid="pop-nav-ai-mba"
              className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-semibold border transition whitespace-nowrap ${
                activeTab === 'ai-mba'
                  ? 'bg-amber-300 text-slate-900 border-amber-300'
                  : 'bg-amber-300/10 text-amber-200 border-amber-300/40 hover:bg-amber-300/20'
              }`}
            >
              <Sparkles className="w-3 h-3" />
              <span className="hidden lg:inline">AI-Enabled MBA</span>
              <span className="inline lg:hidden">AI-MBA</span>
            </Link>
            <NavLink activeTab={activeTab} to="/pop/directory"     id="directory" icon={Search}     label="Directory" />
            <NavLink activeTab={activeTab} to="/pop/opportunities" id="opps"      icon={Briefcase}  label="Opportunities" />
            <NavLink activeTab={activeTab} to="/pop/books"         id="books"     icon={Library}    label="Books" />
            <NavLink activeTab={activeTab} to="/pop/capstone"      id="capstone"  icon={Rocket}     label="Capstone" />
            <NavLink activeTab={activeTab} to="/pop/events"        id="events"    icon={Calendar}   label="Events" />
            <a
              href={academyUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="pop-nav-academy"
              title="SAMANVAI Training Academy — soft skills & career transformation"
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors whitespace-nowrap text-white/70 hover:text-white hover:bg-white/5 group"
            >
              <GraduationCap className="w-3 h-3" />
              Soft Skills
              <ArrowUpRight className="w-2.5 h-2.5 text-white/40 group-hover:text-amber-300 group-hover:-translate-y-px group-hover:translate-x-px transition-all" />
            </a>
            {user && user.role !== 'unassigned' && (
              <NavLink activeTab={activeTab} to="/pop/dashboard" id="dashboard" icon={LayoutDashboard} label="Dashboard" />
            )}

            {/* -------- Overflow: everything else + role-gated items -------- */}
            <MoreMenu
              activeTab={activeTab}
              items={[
                { id: 'library',   to: '/pop/library',    icon: BookOpen,             label: 'Library' },
                { id: 'questions', to: '/pop/questions',  icon: MessageCircleQuestion, label: 'Q&A' },
                ...(user && ['faculty', 'university', 'industry_expert', 'super_admin'].includes(user.role)
                    ? [{ id: 'gaps', to: '/pop/gaps', icon: Compass, label: 'Gaps' }] : []),
                ...(user && ['student', 'faculty', 'industry_expert'].includes(user.role)
                    ? [{ id: 'learning', to: '/pop/learning', icon: PlayCircle, label: 'Learning' }] : []),
                ...(user
                    ? [{ id: 'messages', to: '/pop/messages', icon: MessagesSquare, label: 'Messages' }] : []),
                ...(user?.role === 'super_admin'
                    ? [{ id: 'admin', to: '/pop/admin', icon: ShieldCheck, label: 'Admin' }] : []),
              ]}
            />
          </nav>

          <div className="flex items-center gap-2">
            <Link
              to="/"
              data-testid="pop-back-samanvai"
              className="hidden xl:inline-flex items-center gap-1 text-xs text-white/50 hover:text-white/80 mr-1"
            >
              ← SAMANVAI
            </Link>
            {loading ? (
              <div className="w-16 h-8 rounded bg-white/5 animate-pulse" />
            ) : user ? (
              <UserMenu user={user} logout={logout} />
            ) : (
              <button
                onClick={popLogin}
                data-testid="pop-signin-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-300 hover:bg-amber-200 text-slate-900 text-sm font-semibold shadow-md"
              >
                <Sparkles className="w-3.5 h-3.5" />
                Sign in
              </button>
            )}
          </div>
        </div>
        <PoPCompactNav user={user} activeTab={activeTab} academyUrl={academyUrl} />
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {children}
      </main>

      <footer className="border-t border-white/8 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="text-[10px] tracking-[0.3em] text-white/40">SAMANVAI · PROFESSOR OF PRACTICE · 2026</div>
          <div className="text-[10px] text-white/40 italic">
            Aligned to UGC Professor of Practice framework · Independent verification
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PoPShell;
